import { LoaderFunction, LoaderFunctionArgs } from 'react-router-dom'
import { isWithinInterval } from 'date-fns'

import { eventsApi, ListAbstractReviews } from '../../services/events'
import type { AppStore } from '../../store'
import { AbstractStatus } from '../../types'

export const abstractSubmitListEventsLoader = (store: AppStore) =>
  (async () => {
    const { data: events } = await store.dispatch(
      eventsApi.endpoints.listEvents.initiate(undefined, { forceRefetch: true })
    )

    const currentDate = new Date()
    return events?.filter(event => {
      if (!event.abstract_submission_start || !event.abstract_submission_end) return false

      return isWithinInterval(currentDate, {
        start: event.abstract_submission_start,
        end: event.abstract_submission_end,
      })
    })
  }) satisfies LoaderFunction

export const abstractReviewListEventsLoader = (store: AppStore) =>
  (async () => {
    const { data: events } = await store.dispatch(
      eventsApi.endpoints.listEvents.initiate(undefined, { forceRefetch: true })
    )

    const currentDate = new Date()
    return events?.filter(event => {
      if (!event.abstract_review_start || !event.abstract_review_end) return false

      return isWithinInterval(currentDate, {
        start: event.abstract_review_start,
        end: event.abstract_review_end,
      })
    })
  }) satisfies LoaderFunction

export const listAbstractsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')
    const status = new URL(request.url).searchParams.get('status') as AbstractStatus

    const { data: abstracts } = await store.dispatch(
      eventsApi.endpoints.listAbstracts.initiate(
        {
          eventId: Number(eventId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
          status,
        },
        { forceRefetch: true }
      )
    )

    return abstracts
  }) satisfies LoaderFunction

export const listAbstractReviewsLoader = (store: AppStore) =>
  (async ({ params, request }: LoaderFunctionArgs) => {
    const { eventId } = params
    const pageSize = new URL(request.url).searchParams.get('pageSize')
    const page = new URL(request.url).searchParams.get('page')
    const domain = new URL(request.url).searchParams.get('domain')
    const theme = new URL(request.url).searchParams.get('theme')
    const orderBy = new URL(request.url).searchParams.get(
      'orderBy'
    ) as ListAbstractReviews['orderBy']

    const { data: abstracts } = await store.dispatch(
      eventsApi.endpoints.listAbstractReviews.initiate(
        {
          eventId: Number(eventId),
          pageSize: pageSize ? Number(pageSize) : undefined,
          page: page ? Number(page) : undefined,
          domain: domain ? Number(domain) : null,
          theme: theme ? Number(theme) : null,
          orderBy,
        },
        { forceRefetch: true }
      )
    )

    return abstracts
  }) satisfies LoaderFunction

export const abstractDetailsForReviewLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { abstractId, eventId } = params

    const { data: abstract } = await store.dispatch(
      eventsApi.endpoints.getAbstractDetailsForReview.initiate(
        {
          eventId: Number(eventId),
          id: Number(abstractId),
        },
        { forceRefetch: true }
      )
    )
    const { data: review } = await store.dispatch(
      eventsApi.endpoints.getAbstractReview.initiate(
        {
          eventId: Number(eventId),
          id: Number(abstractId),
        },
        { forceRefetch: true }
      )
    )

    return { abstract, review }
  }) satisfies LoaderFunction

export const abstractLoader = (store: AppStore) =>
  (async ({ params }: LoaderFunctionArgs) => {
    const { abstractId, eventId } = params

    if (abstractId === 'new') {
      return null
    }

    const { data: abstract } = await store.dispatch(
      eventsApi.endpoints.getAbstract.initiate(
        { eventId: Number(eventId), id: Number(abstractId) },
        { forceRefetch: true }
      )
    )

    return abstract
  }) satisfies LoaderFunction
